import React, { useState, useEffect } from 'react';
import './ContactUs.css';
import { API_URL } from './config';

const ContactCard = ({ contact }) => (
  <div className="contact-card">
    <h3>{contact.title}</h3>
    {contact.name && <p className="contact-name">{contact.name}</p>}
    <a href={`mailto:${contact.email}`} className="email-link">
      {contact.email}
    </a>
  </div>
);

function ContactUs() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/contact-us`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => setError('Network error.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="contact-container"><p>Loading…</p></div>;
  if (error) return <div className="contact-container"><p>{error}</p></div>;

  const branches = data?.branches || [];
  // Row-major grid: row 0 is every branch's 1st contact, row 1 is every branch's 2nd, etc. --
  // branches don't need the same number/order of roles, a shorter column just leaves blank
  // cells in later rows.
  const maxRows = Math.max(0, ...branches.map(b => b.contacts.length));

  return (
    <div className="contact-container">
      <h1 className="contact-title">Contact Us</h1>

      <div className="contact-intro">
        <p>Have questions or concerns? Feel free to email us directly using the contacts below.</p>
      </div>

      <div className="contact-shared-row">
        {(data.mailing_address_line1 || data.mailing_address_line2) && (
          <div className="mailing-address-card">
            <h3>Mailing Address</h3>
            <p>
              {data.mailing_address_line1}<br />
              {data.mailing_address_line2}
            </p>
          </div>
        )}

        {data.facebook_url && (
          <div className="social-media-card">
            <h3>Follow Us</h3>
            <a
              href={data.facebook_url}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link facebook"
            >
              <span className="social-icon">f</span>
              <span>Facebook</span>
            </a>
          </div>
        )}
      </div>

      {branches.length > 0 && (
        <div className="contact-roles-section">
          <h2>Contact by Department</h2>

          {/* Desktop: a true row-aligned grid (row-major DOM order, so CSS Grid auto-placement
              lines up e.g. every branch's Principal in the same row). Hidden on mobile in favor
              of the stacked version below -- collapsing this same row-major markup to one column
              would interleave branches (Austin contact, Leander contact, Austin contact, ...)
              instead of grouping each branch together. */}
          <div className="contact-branches-grid" style={{ '--branch-count': branches.length }}>
            {branches.map(b => (
              <h3 className="contact-branch-heading" key={b.branch_id}>{b.branch_name}</h3>
            ))}

            {Array.from({ length: maxRows }).map((_, row) => (
              branches.map(b => {
                const contact = b.contacts[row];
                return contact
                  ? <ContactCard contact={contact} key={`${b.branch_id}-${row}`} />
                  : <div key={`${b.branch_id}-${row}`} />;
              })
            ))}
          </div>

          {/* Mobile: each branch as its own grouped section, contacts in that branch's order. */}
          <div className="contact-branches-stack">
            {branches.map(b => (
              <div className="contact-branch-stack-group" key={b.branch_id}>
                <h3 className="contact-branch-heading">{b.branch_name}</h3>
                {b.contacts.map((contact, i) => <ContactCard contact={contact} key={i} />)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactUs;
